import React, { useState, useContext, useEffect } from "react";
import { API_BASE_URL } from "./api";
import NewTradeForm from "./NewTradeForm";
import TradeList from "./TradeList";
import Analytics from "./Analytics";
import AuthForm from "./AuthForm";
import { AuthContext } from "./AuthContext";
import { ThemeContext } from "./ThemeContext";
import { NavLink, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import {
  Activity,
  Bell,
  CalendarDays,
  ChevronUp,
  CreditCard,
  LayoutDashboard,
  Link2,
  MessagesSquare,
  MoonStar,
  PenSquare,
  Rocket,
  Settings2,
  Shield,
  Sparkles,
  SunMedium,
  Target,
  Users,
  WalletCards,
} from "lucide-react";
import ApiAccessPage from "./pages/ApiAccessPage";
import AdminMessages from "./pages/AdminMessages";
import AdminPendingPayments from "./pages/AdminPendingPayments";
import AdminTools from "./pages/AdminTools";
import AdminSendNewsletter from "./pages/AdminSendNewsletter";
import AdminSubscribers from "./pages/AdminSubscribers";
import DashboardOverviewPage, { getDisplayName, getPlanLabel } from "./pages/DashboardOverviewPage";
import MekFundedAccountsPage from "./pages/MekFundedAccountsPage";
import SupportPage from "./pages/SupportPage";
import TeamManagement from "./TeamManagement";
import AdminPanel from "./AdminPanel";
import Settings from "./Settings";
import AdminToken from "./AdminToken";
import UpgradeDashboard from "./pages/UpgradeDashboard";
import GoalsChallenges from "./pages/GoalsChallenges";
import Calendar from "./pages/Calendar";
import Notifications from "./pages/Notifications";
import Journal from "./pages/Journal";
import Messaging from "./pages/Messaging";
import { DualCurrencyValue, formatCompactNaira, formatCompactUsd, getTradeNgnProfit, getTradeUsdProfit, useExchangeRate } from "./utils/currencyDisplay";

function App() {
  const { refresh, setRefresh, logout, setUser } = useContext(AuthContext);
  const [editTrade, setEditTrade] = useState(null);
  const { token, user } = useContext(AuthContext);
  const { dark, setDark } = useContext(ThemeContext);
  const [userTeams, setUserTeams] = useState([]);
  const [tradeSummary, setTradeSummary] = useState([]);
  const [overviewFeed, setOverviewFeed] = useState([]);
  const [mekFundedAccounts, setMekFundedAccounts] = useState([]);
  const [selectedMekFundedAccount, setSelectedMekFundedAccount] = useState(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    async function refreshWorkspace() {
      if (!token) {
        return;
      }

      try {
        const [meRes, teamRes, tradesRes, feedRes, accountsRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/me`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE_URL}/api/teams`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE_URL}/api/trades`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE_URL}/api/overview-feed`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE_URL}/api/mekfunded/accounts`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const [meData, teamData, tradesData, feedData, accountsData] = await Promise.all([
          meRes.json().catch(() => null),
          teamRes.json().catch(() => null),
          tradesRes.json().catch(() => []),
          feedRes.json().catch(() => ({ items: [] })),
          accountsRes.json().catch(() => ({ accounts: [] })),
        ]);

        if ([meRes, teamRes, tradesRes, feedRes].some((response) => response.status === 401)) {
          logout();
          return;
        }

        // Avoid token churn loops: /api/me may issue a fresh token each call.
        // Keep current token and only refresh user profile details in context.
        if (meData?.user) {
          setUser(meData.user);
        }

        setUserTeams(teamData?.teams || []);
        setTradeSummary(Array.isArray(tradesData) ? tradesData : []);
        setOverviewFeed(Array.isArray(feedData?.items) ? feedData.items : []);
        setMekFundedAccounts(Array.isArray(accountsData?.accounts) ? accountsData.accounts : []);
        // preserve selected account if present in list, otherwise clear
        const initialSelected = Array.isArray(accountsData?.accounts) && accountsData.accounts.length ? accountsData.accounts[0]._id : null;
        setSelectedMekFundedAccount(initialSelected);
      } catch (error) {
        setUserTeams([]);
        setTradeSummary([]);
        setOverviewFeed([]);
        setMekFundedAccounts([]);
      }
    }

    refreshWorkspace();
  }, [token, refresh, setUser, logout]);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 260);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const [filters, setFilters] = useState({
    asset: "",
    winLoss: "",
    dateFrom: "",
    dateTo: "",
    sortBy: "date",
  });

  const isAdmin = user && user.isAdmin;
  const isPaid = user && user.plan && user.plan !== "free";
  const isEnterprise = user && user.plan === "enterprise";
  const hasTeam = userTeams && userTeams.length > 0;
  const canAccessTeamWorkspace = hasTeam || isEnterprise;
  const showUpgrade = user && (user.plan === "free" || user.plan === "pro");
  const { usdToNgnRate } = useExchangeRate();

  const totalProfitUsd = tradeSummary.reduce((sum, trade) => sum + getTradeUsdProfit(trade, usdToNgnRate), 0);
  const totalProfitNgn = tradeSummary.reduce((sum, trade) => sum + getTradeNgnProfit(trade, usdToNgnRate), 0);
  const wins = tradeSummary.filter((trade) => getTradeUsdProfit(trade, usdToNgnRate) > 0).length;
  const winRate = tradeSummary.length ? ((wins / tradeSummary.length) * 100).toFixed(1) : "0.0";
  const thisMonthTrades = tradeSummary.filter((trade) => {
    const createdAt = new Date(trade.created_at);
    const now = new Date();
    return createdAt.getMonth() === now.getMonth() && createdAt.getFullYear() === now.getFullYear();
  }).length;
  const primaryMetrics = [
    {
      label: "Net performance",
      value: (
        <DualCurrencyValue
          usdValue={totalProfitUsd}
          usdToNgnRate={usdToNgnRate}
          compact
          primaryClassName={totalProfitUsd >= 0 ? "text-emerald-300" : "text-rose-300"}
          secondaryClassName="text-xs text-slate-300"
        />
      ),
      helper: "USD first, with naira conversion directly underneath.",
      tone: "",
    },
    {
      label: "Win rate",
      value: `${winRate}%`,
      helper: "A fast read on outcome quality, not a substitute for expectancy.",
      tone: "text-white",
    },
    {
      label: "Trades this month",
      value: String(thisMonthTrades),
      helper: showUpgrade ? "Useful against free-plan capacity and execution consistency." : "Use cadence to judge whether your review process is sustainable.",
      tone: "text-amber-200",
    },
  ];

  const mainNav = [
    { to: "/app", label: "Overview", icon: LayoutDashboard },
    { to: "/app/analytics", label: "Analytics", icon: Activity },
    { to: "/app/newtrade", label: "New Trade", icon: PenSquare },
    { to: "/app/trades", label: "Trades", icon: WalletCards },
    { to: "/app/mekfunded", label: "MekFunded Accounts", icon: Link2 },
    { to: "/app/settings", label: "Settings", icon: Settings2 },
  ];

  const premiumNav = isPaid
    ? [
        { to: "/app/goals-challenges", label: "Goals", icon: Target },
        { to: "/app/calendar", label: "Calendar", icon: CalendarDays },
        { to: "/app/notifications", label: "Alerts", icon: Bell },
        { to: "/app/messaging", label: "Messages", icon: MessagesSquare },
        { to: "/app/journal", label: "Journal", icon: PenSquare },
      ]
    : [];

  const workspaceNav = [
    canAccessTeamWorkspace ? { to: "/app/team", label: "Team", icon: Users } : null,
    showUpgrade ? { to: "/app/upgrade", label: "Upgrade", icon: Rocket } : null,
    isPaid ? { to: "/app/api-access", label: "API", icon: CreditCard } : null,
  ].filter(Boolean);

  const adminNav = isAdmin
    ? [
        { to: "/app/admin-pending-payments", label: "Pending Payments", icon: Shield },
        { to: "/app/admin-panel", label: "Admin Panel", icon: Shield },
        { to: "/app/admin-messages", label: "Admin Messages", icon: MessagesSquare },
        { to: "/app/admin-tools", label: "Admin Tools", icon: Sparkles },
        { to: "/app/admin-send-newsletter", label: "Newsletter", icon: Rocket },
        { to: "/app/admin-subscribers", label: "Subscribers", icon: Users },
        { to: "/app/admin-token", label: "API Tokens", icon: Shield },
      ]
    : [];

  const renderNavSection = (title, items) => {
    if (!items.length) {
      return null;
    }

    return (
      <div className="space-y-3">
        <p className="px-1 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">{title}</p>
        <div className="flex flex-wrap gap-3">
          {items.map((item) => {
            const Icon = item.icon;

            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/app"}
                className={({ isActive }) =>
                  [
                    "inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium transition",
                    isActive
                      ? "border-slate-900 bg-slate-900 text-white shadow-lg shadow-slate-900/20 dark:border-white dark:bg-white dark:text-slate-900"
                      : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-200 dark:hover:bg-gray-700",
                  ].join(" ")
                }
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            );
          })}
        </div>
      </div>
    );
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!token) return <AuthForm />;

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,_#f8f5ef_0%,_#f2ece3_38%,_#eef2f7_100%)] text-slate-900 dark:bg-[linear-gradient(180deg,_#020617_0%,_#111827_45%,_#0f172a_100%)] dark:text-slate-100">
      <div className="mx-auto max-w-7xl px-4 pb-10 pt-5 sm:px-6 lg:px-8">
        <header className="rounded-[28px] border border-slate-200 bg-white/80 px-5 py-5 shadow-sm backdrop-blur dark:border-gray-700 dark:bg-gray-900/75">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between xl:flex-1">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.34em] text-slate-400">MekEdge Workspace</p>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <h1 className="text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Logged-in trading workspace</h1>
                  <span className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-200">
                    {getPlanLabel(user)}
                  </span>
                </div>
                <p className="mt-2 text-sm text-slate-500 dark:text-slate-300">
                  {location.pathname === "/app" ? "Overview" : "Focused module"} for {getDisplayName(user)}.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:justify-end">
                <button
                  onClick={() => setDark(!dark)}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-100 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100 dark:hover:bg-gray-700"
                >
                  {dark ? <SunMedium className="h-4 w-4" /> : <MoonStar className="h-4 w-4" />}
                  {dark ? "Light mode" : "Dark mode"}
                </button>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3 xl:min-w-[360px]">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
                <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">Trades</p>
                <p className="mt-2 text-xl font-semibold text-slate-950 dark:text-white">{tradeSummary.length}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
                <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">Teams</p>
                <p className="mt-2 text-xl font-semibold text-slate-950 dark:text-white">{userTeams.length}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
                <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">P&amp;L</p>
                <div className={`mt-2 text-xl font-semibold ${totalProfitUsd >= 0 ? "text-emerald-600 dark:text-emerald-300" : "text-rose-600 dark:text-rose-300"}`}>
                  {formatCompactUsd(totalProfitUsd)}
                </div>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{formatCompactNaira(totalProfitNgn)}</p>
              </div>
            </div>
          </div>
        </header>

        <div className="mt-6 rounded-[28px] border border-slate-200 bg-white/80 p-4 shadow-sm backdrop-blur dark:border-gray-700 dark:bg-gray-900/75 sm:p-5">
          {renderNavSection("Core", mainNav)}
          <div className="mt-5">{renderNavSection("Premium", premiumNav)}</div>
          <div className="mt-5">{renderNavSection("Workspace", workspaceNav)}</div>
          <div className="mt-5">{renderNavSection("Admin", adminNav)}</div>
        </div>

        <main className="mt-6">
          <Routes>
            <Route path="/analytics" element={<Analytics refresh={refresh} filters={filters} />} />
            <Route path="/settings" element={<Settings />} />
            <Route
              path="/newtrade"
              element={
                <NewTradeForm
                  onSaved={() => {
                    setRefresh((count) => count + 1);
                    navigate("/app/trades");
                    setEditTrade(null);
                  }}
                  editTrade={editTrade}
                  clearEdit={() => {
                    setEditTrade(null);
                    navigate("/app/trades");
                  }}
                />
              }
            />
            <Route
              path="/trades"
              element={
                <TradeList
                  refresh={refresh}
                  onEdit={(trade) => {
                    setEditTrade(trade);
                    navigate("/app/newtrade");
                  }}
                  onRefresh={() => setRefresh((count) => count + 1)}
                  filters={filters}
                  setFilters={setFilters}
                  selectedAccountId={selectedMekFundedAccount}
                />
              }
            />
            <Route
              path="/mekfunded"
              element={
                <MekFundedAccountsPage
                  mekFundedAccounts={mekFundedAccounts}
                  selectedMekFundedAccount={selectedMekFundedAccount}
                  onSelectMekFundedAccount={setSelectedMekFundedAccount}
                />
              }
            />
            {isPaid && (
              <>
                <Route path="/goals-challenges" element={<GoalsChallenges />} />
                <Route path="/calendar" element={<Calendar />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/messaging" element={<Messaging />} />
                <Route path="/journal" element={<Journal />} />
                <Route path="/support" element={<SupportPage />} />
                <Route path="/api-access" element={<ApiAccessPage />} />
              </>
            )}
            {showUpgrade && <Route path="/upgrade" element={<UpgradeDashboard user={user} />} />}
            <Route path="/team" element={<TeamManagement isEnterprise={isEnterprise} />} />
            {isAdmin && (
              <>
                <Route path="/admin-pending-payments" element={<AdminPendingPayments />} />
                <Route path="/admin-panel" element={<AdminPanel />} />
                <Route path="/admin-messages" element={<AdminMessages />} />
                <Route path="/admin-tools" element={<AdminTools />} />
                <Route path="/admin-send-newsletter" element={<AdminSendNewsletter />} />
                <Route path="/admin-subscribers" element={<AdminSubscribers />} />
                <Route path="/admin-token" element={<AdminToken />} />
              </>
            )}
            <Route
              index
              element={
                <DashboardOverviewPage
                  user={user}
                  metrics={primaryMetrics}
                  showUpgrade={showUpgrade}
                  hasTeam={hasTeam}
                  isPaid={isPaid}
                  isAdmin={isAdmin}
                  trades={tradeSummary}
                  overviewFeed={overviewFeed}
                />
              }
            />
          </Routes>
        </main>
      </div>

      <button
        type="button"
        onClick={scrollToTop}
        aria-label="Back to top"
        className={[
          "fixed bottom-24 right-5 z-40 inline-flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white/90 text-slate-800 shadow-lg shadow-slate-900/20 backdrop-blur transition-all duration-200 dark:border-gray-700 dark:bg-gray-900/90 dark:text-slate-100",
          showBackToTop ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-3 opacity-0",
        ].join(" ")}
      >
        <ChevronUp className="h-5 w-5" />
      </button>
    </div>
  );
}

export default App;